type WebMcpTool = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute?: (input: unknown) => unknown;
};

type WebMcpContextProvider = {
  provideContext: (context: { tools: WebMcpTool[] }) => void | Promise<void>;
  registerTool?: (
    tool: WebMcpTool,
    options?: {
      signal?: AbortSignal;
    }
  ) => void;
};

type NavigatorWithWebMcp = Navigator & {
  modelContext?: WebMcpContextProvider;
};

declare global {
  interface Window {
    __sbiWebMcpRegistered?: boolean;
  }
}

function getWebMcpTools(currentPath: string): WebMcpTool[] {
  return [
    {
      name: 'site.current_page',
      description: 'Returns details about the current page route and URL.',
      inputSchema: {
        type: 'object',
        additionalProperties: false,
      },
      execute: () => ({
        path: window.location.pathname,
        href: window.location.href,
        routeState: currentPath,
      }),
    },
    {
      name: 'site.navigate',
      description: 'Navigates the browser to a relative path on this site.',
      inputSchema: {
        type: 'object',
        properties: {
          path: { type: 'string', description: 'Relative site path, for example /services' },
        },
        required: ['path'],
        additionalProperties: false,
      },
      execute: (input) => {
        const value = input as { path?: unknown };
        const path = typeof value?.path === 'string' ? value.path : '/';
        window.location.assign(path);
        return { ok: true, path };
      },
    },
    {
      name: 'site.search_url',
      description: 'Builds a search URL for the site AI search page.',
      inputSchema: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Search query string' },
        },
        required: ['query'],
        additionalProperties: false,
      },
      execute: (input) => {
        const value = input as { query?: unknown };
        const query = typeof value?.query === 'string' ? value.query : '';
        const url = new URL('/search', window.location.origin);
        url.searchParams.set('q', query);
        return { url: url.toString() };
      },
    },
  ];
}

export async function registerWebMcpContext(currentPath: string): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  const navigatorWithWebMcp = window.navigator as NavigatorWithWebMcp;
  const modelContext = navigatorWithWebMcp.modelContext;
  if (!modelContext) {
    return;
  }

  const tools = getWebMcpTools(currentPath);

  if (typeof modelContext.registerTool === 'function') {
    if (window.__sbiWebMcpRegistered) {
      return;
    }

    for (const tool of tools) {
      modelContext.registerTool(tool);
    }
    window.__sbiWebMcpRegistered = true;
    return;
  }

  if (typeof modelContext.provideContext !== 'function') {
    return;
  }

  try {
    await modelContext.provideContext({ tools });
    return;
  } catch {
    // Some shims only accept serializable tool metadata and reject function fields.
    const metadataOnlyTools = tools.map((tool) => ({
      name: tool.name,
      description: tool.description,
      inputSchema: tool.inputSchema,
    }));
    await modelContext.provideContext({ tools: metadataOnlyTools });
  }
}
